import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import Button from 'components/generalUI/Button';
import priceFormat from 'utils/formats/priceFormat';
import styles from './TicketCounter.module.scss';

interface TicketCounterProps {
  adultTickets: number;
  otherTickets: number;
  setAdultTickets: (count: number) => void;
  setOtherTickets: (count: number) => void;
  adultValue: number;
  otherValue: number;
  canAddMoreTickets: boolean;
}

const TicketCounter: FC<TicketCounterProps> = ({
  adultTickets,
  otherTickets,
  setAdultTickets,
  setOtherTickets,
  adultValue,
  otherValue,
  canAddMoreTickets,
}: TicketCounterProps) => {
  const { t } = useTranslation();

  const renderRow = (
    label: string,
    value: number,
    count: number,
    setCount: (next: number) => void
  ) => (
    <div className={styles.Row}>
      <div className={styles.Labels}>
        <p className={styles.Label}>{label}</p>
        <p className={styles.Price}>{priceFormat.format(value)}</p>
      </div>
      <div className={styles.Controls}>
        <Button
          kind="secondary"
          shape="circle"
          className={styles.CounterButton}
          disabled={!count}
          onClick={() => setCount(count - 1)}
          aria-label={t('pages.ticketInformation.removeTicket')}
        >
          <MinusOutlined />
        </Button>
        <span className={styles.Count}>{count}</span>
        <Button
          kind="secondary"
          shape="circle"
          className={styles.CounterButton}
          disabled={!canAddMoreTickets}
          onClick={() => setCount(count + 1)}
          aria-label={t('pages.ticketInformation.addTicket')}
        >
          <PlusOutlined />
        </Button>
      </div>
    </div>
  );

  return (
    <div className={styles.Container}>
      {renderRow(
        t('pages.ticketInformation.adults'),
        adultValue,
        adultTickets,
        setAdultTickets
      )}
      {renderRow(
        t('pages.ticketInformation.others'),
        otherValue,
        otherTickets,
        setOtherTickets
      )}
      <p className={styles.Disclaimer}>
        {t('pages.ticketInformation.childrenEnterForFree')}
      </p>
    </div>
  );
};

export default TicketCounter;
